'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@lib/utils';
import type { CategoryConfig, CategoryKey } from './why-reanalyze-data';
import { RICH_TEXT_TAGS, RICH_TEXT_TAGS_SPACE } from './why-reanalyze-data';

type CategoryListProps = {
  categories: readonly CategoryConfig[];
  activeKey: CategoryKey | null;
  onHover: (key: CategoryKey) => void;
  onLeave: () => void;
  onToggle: (key: CategoryKey) => void;
};

export default function CategoryList({
  categories,
  activeKey,
  onHover,
  onLeave,
  onToggle,
}: CategoryListProps) {
  const t = useTranslations('Reanalysis.whyReanalyze');

  return (
    <ul className="w-full space-y-3 md:space-y-[16px] lg:flex-1">
      {categories.map(item => {
        const isActive = activeKey === item.key;
        const Icon = item.icon;

        return (
          <li
            key={item.key}
            role="button"
            tabIndex={0}
            onMouseEnter={() => onHover(item.key)}
            onMouseLeave={onLeave}
            onFocus={() => onHover(item.key)}
            onBlur={onLeave}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle(item.key);
              }
            }}
            aria-pressed={isActive}
            className={cn(
              'flex cursor-default items-start gap-[12px] rounded-[12px] border p-[13px] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#155dfc] focus-visible:ring-offset-2 md:gap-[16px] md:rounded-[16px] md:p-[17px]',
              isActive
                ? 'scale-[1.02] border-slate-100 bg-slate-50'
                : 'border-transparent bg-transparent hover:border-slate-100 hover:bg-slate-50',
            )}
          >
            <div
              className={cn(
                'flex h-[39px] w-[39px] shrink-0 items-center justify-center rounded-[10px] transition-transform duration-300 md:h-[51px] md:w-[51px] md:rounded-[13px]',
                item.bgColor,
                isActive && 'scale-110',
              )}
            >
              <Icon className={cn('h-5 w-5 md:h-6 md:w-6', item.iconColor)} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <h3
                    className={cn(
                      'text-[15px] font-bold leading-[1.4] transition-colors md:text-[20px]',
                      isActive ? item.iconColor : 'text-[#0f172b]',
                    )}
                  >
                    {t.rich(
                      `categories.${item.key}.name`,
                      RICH_TEXT_TAGS_SPACE,
                    )}
                  </h3>
                  {item.badgeKey ? (
                    <span className="rounded border border-[#e2e8f0] bg-[#f1f5f9] px-1.5 py-0.5 text-[8px] font-bold text-[#90a1b9] md:text-[11px]">
                      {t(item.badgeKey)}
                    </span>
                  ) : null}
                </div>
                <span
                  className={cn(
                    'rounded-full px-2.5 py-1 text-[11px] font-bold md:text-[15px]',
                    item.bgColor,
                    item.iconColor,
                  )}
                >
                  {item.value}
                </span>
              </div>
              <p className="whitespace-pre-line text-[12px] leading-[18px] text-[#62748e] md:text-[16px] md:leading-[24px]">
                {t.rich(`categories.${item.key}.description`, RICH_TEXT_TAGS)}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
